'use client';

import { Locale } from '@/lib/i18n/dictionaries';
import { useTranslation } from '@/lib/i18n/i18n.client';
import { cn } from '@/lib/utils';

import { env } from '@/constant/process';

import type { PromoBadgeStrapi } from './utils';
import { useIsOutdated } from './useIsOutdated';

import './PromoBadge.css';

export type { PromoBadgeStrapi } from './utils';

const PROMO_BADGE_LOCALES: Locale[] = ['zh-cn'];

const NUMBER_PATTERN = /(\d+(?:\.\d+)*)/;

type PromoBadgeProps = {
  badge?: PromoBadgeStrapi | null;
  classNames?: {
    root?: string;
  };
};

export function PromoBadge({ badge, classNames }: PromoBadgeProps) {
  const { texts } = useTranslation();
  const isOutdated = useIsOutdated(badge?.showUntil);

  if (
    !badge?.text ||
    !badge.showUntil ||
    !PROMO_BADGE_LOCALES.includes(env.publicLocale) ||
    env.isVisualTest ||
    isOutdated
  ) {
    return null;
  }

  const parts = badge.text.split(NUMBER_PATTERN).filter(Boolean);

  return (
    <span
      className={cn(
        'promo-badge relative inline-flex shrink-0 items-center gap-1 overflow-hidden whitespace-nowrap rounded-l-md rounded-r-xl pl-1.5 pr-2.5 align-middle text-body-s font-black leading-none tracking-wide',
        classNames?.root
      )}
    >
      <span
        aria-hidden
        className='promo-badge__coin shrink-0 rounded-full'
      >
        <svg viewBox='0 0 15 15' className='size-full'>
          <text
            x='7.5'
            y='7.5'
            textAnchor='middle'
            dominantBaseline='central'
            className='promo-badge__coin-text text-label-s font-black tracking-normal'
          >
            {texts.promo_badge.coin_label}
          </text>
        </svg>
      </span>
      {parts.map((part, index) =>
        NUMBER_PATTERN.test(part) ? (
          <span key={index} className='promo-badge__number font-mono font-bold'>
            {part}
          </span>
        ) : (
          <span key={index}>{part.trim()}</span>
        )
      )}
      <span
        aria-hidden
        className='promo-badge__shine pointer-events-none absolute inset-y-0 left-0 w-2/5'
      />
    </span>
  );
}
